import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore, hasMinimumRole, ROLES } from '../lib/auth';
import { departmentAPI, venueAPI } from '../lib/api';
import {
  Building2,
  Plus,
  Trash2,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Layers,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errorMessage';

const ROLE_OPTIONS = [
  { value: 'manager', label: 'Manager' },
  { value: 'head_chef', label: 'Head Chef' },
  { value: 'gm', label: 'General Manager' },
  { value: 'ops', label: 'Operations' },
  { value: 'staff', label: 'Staff' },
];

const CreateDepartmentDialog = ({ venueId, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', manager_role: 'manager' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Department name is required');
      return;
    }
    setLoading(true);
    try {
      await departmentAPI.create(venueId, formData);
      toast.success('Department created');
      setOpen(false);
      setFormData({ name: '', manager_role: 'manager' });
      onCreated();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-rtb">
          <Plus className="w-4 h-4 mr-2" />
          Add Department
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#18181B] border border-[#3F3F46] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase tracking-tight">
            New Department
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Department Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Kitchen, Bar, Security…"
              required
              className="input-rtb mt-1"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-sm font-bold uppercase text-[#A1A1AA]">
              Default Assignee Role *
            </Label>
            <p className="text-xs text-[#71717A] mb-1">
              Failed checklist tasks for this department auto-assign to a venue user with this role.
            </p>
            <Select
              value={formData.manager_role}
              onValueChange={(val) => setFormData({ ...formData, manager_role: val })}
            >
              <SelectTrigger className="input-rtb mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#18181B] border border-[#3F3F46] text-white">
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1 border-[#3F3F46] text-[#A1A1AA]"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" className="btn-rtb flex-1" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const DepartmentRow = ({ dept, canManage, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Delete department "${dept.name}"? Tasks assigned to it will remain but lose their department link.`)) return;
    setDeleting(true);
    try {
      await departmentAPI.delete(dept.id);
      toast.success('Department deleted');
      onDeleted();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const roleLabel = ROLE_OPTIONS.find((r) => r.value === dept.manager_role)?.label ?? dept.manager_role;

  return (
    <div className="card-rtb flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="p-2 rounded-md bg-[#27272A]">
          <Layers className="w-5 h-5 text-[#FF6600]" />
        </div>
        <div>
          <p className="text-white font-semibold">{dept.name}</p>
          <p className="text-[#A1A1AA] text-xs mt-0.5">
            Auto-assigns to: <span className="text-[#FF6600] font-medium">{roleLabel}</span>
          </p>
        </div>
      </div>
      {canManage && (
        <Button
          type="button"
          variant="outline"
          className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 px-3"
          onClick={handleDelete}
          disabled={deleting}
          data-testid={`delete-dept-${dept.id}`}
        >
          {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </Button>
      )}
    </div>
  );
};

const DepartmentsPage = () => {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [venue, setVenue] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const canManage = hasMinimumRole(user, ROLES.GM);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [venueRes, deptRes] = await Promise.all([
        venueAPI.get(venueId),
        departmentAPI.list(venueId),
      ]);
      setVenue(venueRes.data);
      setDepartments(deptRes.data || []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (venueId) fetchData();
  }, [venueId]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          type="button"
          variant="outline"
          className="border-[#3F3F46] text-[#A1A1AA] px-3"
          onClick={() => navigate('/venues')}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mb-1">
            <Building2 className="w-4 h-4" />
            <span>{venue?.name ?? '…'}</span>
          </div>
          <h1 className="font-heading text-3xl font-black text-white uppercase tracking-tight">
            Departments
          </h1>
        </div>
        {canManage && !loading && !error && (
          <CreateDepartmentDialog venueId={venueId} onCreated={fetchData} />
        )}
      </div>

      {/* Info banner */}
      <div className="p-4 bg-[#27272A] rounded-lg border border-[#3F3F46] text-sm text-[#A1A1AA]">
        Departments map checklist items to the role responsible for fixing failures.
        When a checklist item fails, a task is automatically routed to the first available
        user in this venue with the matching role.
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF6600]" />
        </div>
      ) : error ? (
        <div className="card-rtb flex items-center gap-3 text-[#FF3B30]">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      ) : departments.length === 0 ? (
        <div className="card-rtb text-center py-12">
          <Layers className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">
            No Departments Yet
          </h3>
          <p className="text-[#A1A1AA] mb-6">
            Add departments to enable automatic task routing from checklist failures.
          </p>
          {canManage && (
            <CreateDepartmentDialog venueId={venueId} onCreated={fetchData} />
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {departments.map((dept) => (
            <DepartmentRow
              key={dept.id}
              dept={dept}
              canManage={canManage}
              onDeleted={fetchData}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DepartmentsPage;
